<template>
  <div>
    <navbar :links="links" />
    <div
      :class="{
        'desktop-mode': $store.state.common.desktopMode,
        'elevation-1': scrollTop > fixedSearchThreshold,
        firefox: $store.state.common.runtimeEnv.firefox,
        safari: $store.state.common.runtimeEnv.safari,
        tools: scrollTop <= fixedSearchThreshold,
        'tools-fixed': scrollTop > fixedSearchThreshold,
        'visible-drawer': $store.state.common.showDrawer,
      }"
    >
      <v-layout align-center>
        <!-- Search -->
        <v-text-field
          ref="searchbar"
          v-model="searchText"
          class="searchbar-input"
          style="margin-left: auto"
          single-line
          hide-details
          :label="searchLabel"
          clearable
          append-icon="search"
          @click:append="focusSearch()"
          @click:clear="clearSearch()"
          @keyup.native.esc="clearSearch()"
        />
        <div style="flex-grow: 0; margin-left: auto">
          <v-checkbox
            v-model="allSelected"
            class="msr--text ml-3"
            color="msr"
            hide-details
          />
        </div>
      </v-layout>
    </div>
    <div
      v-if="scrollTop > fixedSearchThreshold"
      class="tools-fixed-spacer"
    />
    <!-- Channel we are currently activating -->
    <v-layout
      justify-end
      align-center
    >
      <mini-radio-channel-tile-action
        v-if="channel"
        class="mr-1 mb-2"
        cover-size="small"
        :channel="channel"
        label="currentChannelBeingActivated"
      />
    </v-layout>
    <!-- No players available warning -->
    <v-list v-if="!loading && decoders && !filteredStoreDecoderWrappers.length">
      <v-subheader>{{ $t('noAvailableDecoders') }}</v-subheader>
    </v-list>
    <!-- Decoder list -->
    <selectable-store-decoders
      v-for="(wrapper, wrapperIndex) in filteredStoreDecoderWrappers"
      ref="selectableStoreDecoders"
      :key="wrapper.store.id"
      :store="wrapper.store"
      :decoders="wrapper.decoders"
      :preselect="decoders.length === 1"
      :show-divider="wrapperIndex < filteredStoreDecoderWrappers.length - 1"
    >
      <!-- Mini current RadioChannel Info -->
      <mini-radio-channel-tile-action
        slot="decoder"
        slot-scope="scope"
        class="pb-1"
        :channel="scope.decoder.channel"
        :highlighted="decoderIdentToSelected[scope.decoder.ident]"
        :show-label="$vuetify.breakpoint.smAndUp"
      />
    </selectable-store-decoders>
    <!-- Empty placeholder to allow a bit more scrolling -->
    <div style="height: 100px" />
    <!-- Add selected Decoders button -->
    <v-slide-y-reverse-transition>
      <v-btn
        v-if="atLeastOneDecoderSelected && !showConfirmationDialog"
        dark
        round
        fixed
        bottom
        right
        data-cy="continue-btn"
        :class="$desktopModeStyles"
        class="msr z-index-1"
        @click.stop="showConfirmationDialog = true"
      >
        <v-icon left>
          arrow_forward
        </v-icon>
        {{ $t('goForward') }}
      </v-btn>
    </v-slide-y-reverse-transition>
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <!-- Confirmation Dialog -->
    <entry-creation-dialog
      ref="confirmationDialog"
      :show.sync="showConfirmationDialog"
      :title="dialogTitle"
      :busy="adding"
      :allow-fullscreen="false"
      :show-spot-options="false"
      :show-weekday-options="false"
      :show-timeframe-controls="false"
      @aborted="handleDialogAbortion()"
      @confirmed="handleDialogConfirmation()"
    />
  </div>
</template>

<script>
import addDecoderTo from '@/mixins/addDecoderTo.js';
import trackScrollTop from '@/mixins/trackScrollTop.js';

import EntryCreationDialog from '@/components/EntryCreationDialog';
import MiniRadioChannelTileAction from '@/components/MiniRadioChannelTileAction';
import Navbar from '@/components/Navbar';
import SelectableStoreDecoders from '@/components/SelectableStoreDecoders';

export default {
  name: 'Adddecodertochannel',
  components: {
    EntryCreationDialog,
    MiniRadioChannelTileAction,
    Navbar,
    SelectableStoreDecoders,
  },
  mixins: [trackScrollTop, addDecoderTo],
  data() {
    return {
      decoders: [],
      links: ['music', 'addDecoderToChannel'],
      channel: null,
    };
  },
  computed: {
    channelId() {
      return this.$route.params.channelId;
    },
    radioChannels() {
      return this.$store.state.common.radioChannels;
    },
  },
  mounted() {
    this.loading = true;
    this.fetchChannel().then(() => {
      this.updateNavbarLinks(this.channel);
      this.fetchUserRadioDecodersForChannel(this.channel).then(() => {
        this.loading = false;
      });
    });
  },
  methods: {
    updateNavbarLinks(channel) {
      const link = {
        label: channel.name,
        path: '/channel/' + channel.id,
      };
      const lastLink = {
        label: 'addDecoderToChannel',
        active: false,
      };
      this.links = ['music', link, lastLink];
    },
    handleDialogConfirmation() {
      const decoders = this.getSelectedDecoders();
      this.makeDecodersUseChannel(decoders);
    },
    makeDecodersUseChannel(decoders) {
      const decoderIds = decoders.map((decoder) => decoder.id);
      this.adding = true;
      this.$api
        .activateChannelOnMultipleRadioDecoders(this.channelId, decoderIds)
        .then((response) => {
          const template = this.$t('radioChannelUpdateSuccess');
          const message = template.replace(
            '{{ channelName }}',
            this.channel.name
          );
          this.$bus.$emit('alert-success', {
            message: message,
            translate: false,
            timeout: 8000,
          });

          this.adding = false;
          // Go back to Channel which we activated.
          this.$store.commit('UNSET_PRE_ROUTE_QUESTION');
          this.goToChannel(this.channelId);
        });
    },
    goToChannel(channelId) {
      const path = '/channel/' + channelId;
      this.$router.push(path);
    },
    fetchChannel() {
      return this.$api.retrieveRadioChannel(this.channelId).then((response) => {
        this.channel = response.data;
      });
    },
    fetchUserRadioDecodersForChannel(channel) {
      const promise = this.$api.listUserRadioDecodersForChannel(channel.id);
      promise.then((response) => {
        this.decoders = response.data;
      });
      return promise;
    },
  },
};
</script>

<style src="@/mixins/addDecoderTo.global.css"></style>

<style scoped src="@/mixins/addDecoderTo.scoped.css"></style>



// WEBPACK FOOTER //
// src/pages/AddDecoderToChannel.vue